import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, X, Plus, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { uploadFile, fileUrl, errMsg } from "@/lib/api";

const UploadBtn = ({ accept, multiple, onFiles, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadFile(f));
      onFiles(urls);
      toast.success("Upload complete");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept={accept} multiple={multiple} className="hidden" onChange={pick} data-testid={`${testid}-file`} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="inline-flex items-center gap-2 border border-gold text-gold px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-gold hover:text-navy transition-colors shrink-0" data-testid={`${testid}-upload`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload
      </button>
    </>
  );
};

const Rows = ({ field, value, onChange, testid }) => {
  const rows = Array.isArray(value) ? value : [];
  const set = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2" data-testid={testid}>
      {rows.map((r, i) => (
        <div key={i} className="flex gap-2">
          {field.columns.map(([k, l]) => <input key={k} placeholder={l} className="field !py-2 min-w-0" value={r[k] || ""} onChange={(e) => set(i, k, e.target.value)} data-testid={`${testid}-${i}-${k}`} />)}
          <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))} className="px-2 text-slate-400 hover:text-red-400" aria-label="Remove row"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...rows, {}])} className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-gold" data-testid={`${testid}-add-row`}><Plus className="h-3.5 w-3.5" /> Add row</button>
    </div>
  );
};

export const FieldInput = ({ field: f, value, onChange, prefix = "field" }) => {
  const testid = `${prefix}-${f.k}`;
  switch (f.type) {
    case "textarea":
      return <textarea rows={f.rows || 4} className="field resize-y" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
    case "bool":
      return <div className="flex items-center gap-3"><Switch checked={!!value} onCheckedChange={onChange} data-testid={testid} /><span className="text-sm text-slate-300">{value ? "Yes" : "No"}</span></div>;
    case "select":
      return (
        <select className="field" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
          <option value="">Select…</option>
          {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    case "number":
      return <input type="number" className="field" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} data-testid={testid} />;
    case "date":
      return <input type="date" className="field" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
    case "list":
      return <textarea rows={3} className="field" value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n"))} onBlur={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} data-testid={testid} />;
    case "rows":
      return <Rows field={f} value={value} onChange={onChange} testid={testid} />;
    case "images":
      return (
        <div className="space-y-2" data-testid={testid}>
          <div className="flex flex-wrap gap-2">
            {(value || []).map((u, i) => (
              <div key={u + i} className="relative h-20 w-20 bg-pitch border border-line">
                <img src={fileUrl(u)} alt="" className="h-full w-full object-cover" />
                <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="absolute -right-2 -top-2 bg-red-600 p-0.5" aria-label="Remove image"><X className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
          <UploadBtn accept="image/*" multiple onFiles={(urls) => onChange([...(value || []), ...urls])} testid={testid} />
        </div>
      );
    case "image":
    case "media":
      return (
        <div className="flex items-center gap-3">
          {f.type === "image" && value && <img src={fileUrl(value)} alt="" className="h-14 w-14 object-cover border border-line shrink-0" />}
          <input className="field !py-2" placeholder="Paste a URL or upload" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
          <UploadBtn accept={f.type === "image" ? "image/*" : "image/*,video/*"} onFiles={([u]) => onChange(u)} testid={testid} />
        </div>
      );
    default:
      return <input className="field" value={value ?? ""} onChange={(e) => onChange(e.target.value)} required={f.required} data-testid={testid} />;
  }
};
